import React, { useState, useEffect } from 'react'
import { useAuth } from '../../../shared/context/AuthContext'

interface InvitationRequest {
  id: string
  companyName?: string
  contactFirstName?: string
  contactLastName?: string
  email?: string
  phone?: string
  status: 'pending' | 'approved' | 'declined' | 'completed'
  adminNotes?: string
  approvedBy?: string
  approvedAt?: string
  createdAt: string
  updatedAt: string
  // Allow for dynamic fields
  [key: string]: any
}

const InvitationRequests: React.FC = () => {
  const { user } = useAuth()
  const [invitations, setInvitations] = useState<InvitationRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Check if current user is master admin
  const isMasterAdmin = user?.role === 'master_admin'

  // Helper function to extract display fields from dynamic invitation data
  const extractDisplayFields = (invitation: InvitationRequest) => {
    // Try to find email field (could be stored with different field names)
    const email = invitation.email || 
                 invitation.contact_email || 
                 invitation.business_email || 
                 invitation.primary_email || 
                 'No email'

    // Try to find company name (could be stored with different field names)
    const companyName = invitation.companyName || 
                       invitation.company_name || 
                       invitation.businessName || 
                       invitation.business_name ||
                       'No company name'

    // Try to find first name (could be stored with different field names)
    const contactFirstName = invitation.contactFirstName || 
                            invitation.contact_first_name || 
                            invitation.firstName || 
                            invitation.first_name ||
                            ''

    // Try to find last name (could be stored with different field names)
    const contactLastName = invitation.contactLastName || 
                          invitation.contact_last_name || 
                          invitation.lastName || 
                          invitation.last_name ||
                          ''

    // Try to find phone (could be stored with different field names)
    const phone = invitation.phone || 
                 invitation.contact_phone || 
                 invitation.business_phone || 
                 invitation.primary_phone ||
                 'No phone'

    return {
      email,
      companyName,
      contactFirstName,
      contactLastName,
      phone,
      fullName: contactFirstName && contactLastName 
                ? `${contactFirstName} ${contactLastName}` 
                : contactFirstName || contactLastName || 'No contact name'
    }
  }
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [currentInvitation, setCurrentInvitation] = useState<InvitationRequest | null>(null)
  const [adminNotes, setAdminNotes] = useState('')
  const [actionType, setActionType] = useState<'approve' | 'decline' | null>(null)
  const [salesforceUsers, setSalesforceUsers] = useState<Array<{ id: string; name: string; email: string }>>([])
  const [selectedUnderwritingOwner, setSelectedUnderwritingOwner] = useState('')
  const [loadingSalesforceUsers, setLoadingSalesforceUsers] = useState(false)

  useEffect(() => {
    fetchInvitations()
  }, [selectedStatus])

  const fetchInvitations = async () => {
    try {
      setLoading(true)
      const url = selectedStatus === 'all' 
        ? '/api/admin/invitations'
        : `/api/admin/invitations?status=${selectedStatus}`
      
      const response = await fetch(url, {
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json()
        setInvitations(data.invitations || [])
      } else {
        const errorData = await response.json()
        setError(errorData.message || 'Failed to fetch invitation requests')
      }
    } catch (err) {
      console.error('Error fetching invitations:', err)
      setError('Unable to fetch invitation requests. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleActionClick = async (invitation: InvitationRequest, action: 'approve' | 'decline') => {
    setCurrentInvitation(invitation)
    setActionType(action)
    setAdminNotes('')
    setSelectedUnderwritingOwner('')
    
    // Load Salesforce users for approval action
    if (action === 'approve' && isMasterAdmin) {
      await loadSalesforceUsers()
    }
    
    setShowNotesModal(true)
  }
  
  const loadSalesforceUsers = async () => {
    try {
      setLoadingSalesforceUsers(true)
      const response = await fetch('/api/admin/invitations/salesforce-users', {
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        setSalesforceUsers(data.users || [])
      } else {
        console.error('Failed to load Salesforce users')
        setSalesforceUsers([])
      }
    } catch (error) {
      console.error('Error loading Salesforce users:', error)
      setSalesforceUsers([])
    } finally {
      setLoadingSalesforceUsers(false)
    }
  }

  const handleDeleteClick = (invitation: InvitationRequest) => {
    setCurrentInvitation(invitation)
    setShowDeleteModal(true)
  }

  const handleConfirmAction = async () => {
    if (!currentInvitation || !actionType) return
    
    // Validate underwriting owner selection for approvals
    if (actionType === 'approve' && !selectedUnderwritingOwner) {
      setError('Please select an underwriting owner before approving the invitation.')
      return
    }

    setProcessingId(currentInvitation.id)
    setError(null)

    try {
      const endpoint = actionType === 'approve' 
        ? `/api/admin/invitations/${currentInvitation.id}/approve`
        : `/api/admin/invitations/${currentInvitation.id}/decline`

      const requestBody = actionType === 'approve' 
        ? { adminNotes, underwritingOwnerId: selectedUnderwritingOwner }
        : { adminNotes }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      })

      if (response.ok) {
        const data = await response.json()
        
        // Update the invitation in the list
        setInvitations(prev => prev.map(inv => 
          inv.id === currentInvitation.id ? data.invitation : inv
        ))
        
        setShowNotesModal(false)
        setCurrentInvitation(null)
        setActionType(null)
        setAdminNotes('')
      } else {
        const errorData = await response.json()
        setError(errorData.message || `Failed to ${actionType} invitation`)
      }
    } catch (err) {
      console.error(`Error ${actionType}ing invitation:`, err)
      setError(`Unable to ${actionType} invitation. Please try again.`)
    } finally {
      setProcessingId(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (!currentInvitation) return

    setDeletingId(currentInvitation.id)
    setError(null)

    try {
      const response = await fetch(`/api/admin/invitations/${currentInvitation.id}`, {
        method: 'DELETE',
        credentials: 'include'
      })

      if (response.ok) {
        // Remove the invitation from the list
        setInvitations(prev => prev.filter(inv => inv.id !== currentInvitation.id))
        
        setShowDeleteModal(false)
        setCurrentInvitation(null)
      } else {
        const errorData = await response.json()
        setError(errorData.message || 'Failed to delete invitation')
      }
    } catch (err) {
      console.error('Error deleting invitation:', err)
      setError('Unable to delete invitation. Please try again.')
    } finally {
      setDeletingId(null)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusMap = {
      pending: { class: 'warning', text: 'Pending Review' },
      approved: { class: 'success', text: 'Approved' },
      declined: { class: 'danger', text: 'Declined' },
      completed: { class: 'info', text: 'Account Created' }
    }
    const statusInfo = statusMap[status as keyof typeof statusMap] || { class: 'secondary', text: status }
    return <span className={`badge bg-${statusInfo.class}`}>{statusInfo.text}</span>
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading invitation requests...</p>
      </div>
    )
  }

  return (
    <div>
      {/* Page Header */}
      <div className="admin-content-header">
        <h2 className="admin-page-title">Invitation Requests</h2>
        <button 
          className="btn btn-outline-primary"
          onClick={fetchInvitations}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Refreshing...
            </>
          ) : (
            'Refresh'
          )}
        </button>
      </div>

      {/* Page Description */}
      <div className="px-4 py-3 bg-light border-bottom">
        <p className="mb-0 text-muted">
          Review and manage company invitation requests. 
          {isMasterAdmin 
            ? 'Approve qualified companies to create accounts or decline applications that don\'t meet requirements.'
            : 'View invitation requests and their status. Only Master Admins can approve or decline requests.'
          }
        </p>
        {!isMasterAdmin && (
          <div className="alert alert-info mt-2 mb-0" role="alert">
            <i className="bi bi-info-circle me-2"></i>
            <strong>Limited Access:</strong> You can view invitation requests but cannot approve or decline them. 
            Contact a Master Admin to process pending requests.
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="card mb-0">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Filter by Status</label>
              <select 
                className="form-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="all">All Requests</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="declined">Declined</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div className="col-md-9">
              <label className="form-label">Statistics</label>
              <div className="d-flex gap-3">
                <small className="text-muted">
                  Total: <strong>{invitations.length}</strong>
                </small>
                <small className="text-muted">
                  Pending: <strong>{invitations.filter(inv => inv.status === 'pending').length}</strong>
                </small>
                <small className="text-muted">
                  Approved: <strong>{invitations.filter(inv => inv.status === 'approved').length}</strong>
                </small>
                <small className="text-muted">
                  Completed: <strong>{invitations.filter(inv => inv.status === 'completed').length}</strong>
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger mx-4 mt-3" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {/* Invitations Table */}
      <div className="card">
        <div className="card-body p-0">
          {invitations.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-3">
                <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
              </div>
              <h5 className="text-muted">No invitation requests found</h5>
              <p className="text-muted">
                {selectedStatus === 'all' 
                  ? 'No invitation requests have been submitted yet.'
                  : `No ${selectedStatus} invitation requests found.`
                }
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th>Actions</th>
                  </tr>
                </thead>
            <tbody>
              {invitations.map((invitation) => {
                const displayFields = extractDisplayFields(invitation)
                return (
                <tr key={invitation.id}>
                  <td>
                    <strong>{displayFields.companyName}</strong>
                  </td>
                  <td>
                    {displayFields.fullName}
                  </td>
                  <td>
                    <a href={`mailto:${displayFields.email}`} className="text-decoration-none">
                      {displayFields.email}
                    </a>
                  </td>
                  <td>
                    <a href={`tel:${displayFields.phone}`} className="text-decoration-none">
                      {displayFields.phone}
                    </a>
                  </td>
                  <td>
                    {getStatusBadge(invitation.status)}
                  </td>
                  <td>
                    <small>{formatDate(invitation.createdAt)}</small>
                  </td>
                  <td>
                    {invitation.status === 'pending' ? (
                      isMasterAdmin ? (
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-success btn-sm px-3"
                            onClick={() => handleActionClick(invitation, 'approve')}
                            disabled={processingId === invitation.id}
                          >
                            {processingId === invitation.id ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                Processing...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-check me-1"></i>
                                Approve
                              </>
                            )}
                          </button>
                          <button
                            className="btn btn-danger btn-sm px-3"
                            onClick={() => handleActionClick(invitation, 'decline')}
                            disabled={processingId === invitation.id}
                          >
                            <i className="bi bi-x me-1"></i>
                            Decline
                          </button>
                        </div>
                      ) : (
                        <small className="text-muted">
                          <i className="bi bi-lock me-1"></i>
                          Master Admin required
                        </small>
                      )
                    ) : (
                      <button
                        className="btn btn-outline-danger btn-sm px-3"
                        onClick={() => handleDeleteClick(invitation)}
                        disabled={deletingId === invitation.id}
                      >
                        {deletingId === invitation.id ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                            Deleting...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-trash me-1"></i>
                            Delete
                          </>
                        )}
                      </button>
                    )}
                  </td>
                </tr>
                )
              })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Notes Modal */}
      {showNotesModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {actionType === 'approve' ? 'Approve' : 'Decline'} Invitation Request
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowNotesModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {currentInvitation && (
                  <>
                    <div className="mb-3">
                      {(() => {
                        const displayFields = extractDisplayFields(currentInvitation)
                        return (
                          <>
                            <strong>Company:</strong> {displayFields.companyName}<br />
                            <strong>Contact:</strong> {displayFields.fullName}<br />
                            <strong>Email:</strong> {displayFields.email}
                          </>
                        )
                      })()}
                    </div>
                    
                    {actionType === 'approve' && (
                      <div className="mb-3">
                        <label htmlFor="underwritingOwner" className="form-label">
                          Underwriting Owner <span className="text-danger">*</span>
                        </label>
                        {loadingSalesforceUsers ? (
                          <div className="d-flex align-items-center">
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            <span className="text-muted">Loading Salesforce users...</span>
                          </div>
                        ) : (
                          <select
                            className="form-select"
                            id="underwritingOwner"
                            value={selectedUnderwritingOwner}
                            onChange={(e) => setSelectedUnderwritingOwner(e.target.value)}
                            required
                          >
                            <option value="">Select an underwriting owner...</option>
                            {salesforceUsers.map(user => (
                              <option key={user.id} value={user.id}>
                                {user.name} ({user.email})
                              </option>
                            ))}
                          </select>
                        )}
                        {salesforceUsers.length === 0 && !loadingSalesforceUsers && (
                          <div className="form-text text-warning">
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            No Salesforce users found. Please check your Salesforce connection.
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="mb-3">
                      <label htmlFor="adminNotes" className="form-label">
                        {actionType === 'approve' ? 'Approval Notes (Optional)' : 'Reason for Decline'}
                      </label>
                      <textarea
                        className="form-control"
                        id="adminNotes"
                        rows={3}
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        placeholder={
                          actionType === 'approve' 
                            ? 'Add any notes about this approval...'
                            : 'Please provide a reason for declining this request...'
                        }
                      />
                    </div>

                    <div className={`alert alert-${actionType === 'approve' ? 'success' : 'warning'}`}>
                      <h6 className="alert-heading">
                        {actionType === 'approve' ? 'Approval Process' : 'Decline Process'}
                      </h6>
                      <p className="mb-0">
                        {actionType === 'approve' 
                          ? 'The user will receive an email with a secure link to complete their account setup.'
                          : 'The user will receive an email notification that their request has been declined.'
                        }
                      </p>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowNotesModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className={`btn btn-${actionType === 'approve' ? 'success' : 'danger'}`}
                  onClick={handleConfirmAction}
                  disabled={processingId !== null}
                >
                  {processingId !== null ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className={`bi bi-${actionType === 'approve' ? 'check' : 'x'} me-2`}></i>
                      {actionType === 'approve' ? 'Approve Request' : 'Decline Request'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-trash me-2"></i>
                  Delete Invitation Request
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeleteModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {currentInvitation && (
                  <>
                    <div className="alert alert-warning">
                      <h6 className="alert-heading">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        Permanent Deletion
                      </h6>
                      <p className="mb-0">
                        This action cannot be undone. The invitation request will be permanently removed from the system.
                      </p>
                    </div>
                    
                    <div className="mb-3">
                      <strong>Company:</strong> {currentInvitation.companyName}<br />
                      <strong>Contact:</strong> {currentInvitation.contactFirstName} {currentInvitation.contactLastName}<br />
                      <strong>Email:</strong> {currentInvitation.email}<br />
                      <strong>Status:</strong> {getStatusBadge(currentInvitation.status)}
                    </div>

                    <p>Are you sure you want to delete this invitation request?</p>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deletingId !== null}
                >
                  {deletingId !== null ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash me-2"></i>
                      Delete Request
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default InvitationRequests